import * as THREE from 'three';
import { operatorFor } from '../gameplay/operators.js';
import { NetInput } from './clientInput.js';
import { eyeOffset } from '../player/PlayerController.js';
import { EXPLOSIVES, STREAK_RULES } from '../modes/rules.js';

/**
 * Glue between the running game and the server.
 *
 * The single-player game drives `player` straight from the keyboard. Online,
 * the same controller is driven from a COMMAND — which is then also sent to the
 * server and kept for replay. That is the only change to how the local player
 * moves, and it is why prediction reconciles: the server runs this exact
 * controller over the exact same commands.
 *
 * Everyone else is a body with no brain: a soldier rig positioned from the
 * interpolation buffer each frame. No AI, no physics, no thinking — the server
 * already decided where they are.
 */

const _v = new THREE.Vector3();
const _prev = new THREE.Vector3();
const _from = new THREE.Vector3();

export class NetSession {
  /**
   * @param {import('./NetClient.js').NetClient} client  already connected
   * @param {object} game
   */
  constructor(client, game) {
    this.client = client;
    this.game = game;
    this.input = new NetInput();
    /** id -> { rig, lastSeen } */
    this.bodies = new Map();
    this._pingAcc = 0;
    this._lastYaw = 0;
    this._lastPitch = 0;

    client.onClose = () => {
      game.hud?.showObjective?.('DISCONNECTED', 'THE SERVER CLOSED THE CONNECTION', 8);
    };
    // A side change lands at a round start. Everything that asks "is this
    // person a friend" reads `player.team`, so it has to follow.
    // The ledger is the server's; mirror it into the local object so the HUD
    // rail and the F key read the same thing the server will honour.
    client.onFlashed = (k) => {
      game.flash = Math.max(game.flash ?? 0, k);
    };
    client.onStreaks = (held) => {
      game.streaks?.setHeld(held);
      const top = game.streaks?.ready;
      if (top && held.length) {
        game.hud.streakToast = { name: top.name, sub: 'READY — PRESS F', t: 0 };
        game.audio?.streakReady?.();
      }
    };
    client.onTeamChange = (team) => {
      game.player.team = team;
      game.playerTeam = team;
      // The operator slot is an index into YOUR SIDE's six, so changing sides
      // changes which operator that index means. Re-resolving here keeps the
      // award podium showing a soldier from the side you are actually on.
      game.playerOperator = operatorFor(game.playerOp, { team, ffa: false });
      game.player.operator = game.playerOperator;
      game.net?.client?.setOperator?.(game.playerOp);
      game.hud?.showObjective?.(
        team === 'red' ? 'ATTACKERS' : 'DEFENDERS', 'YOU CHANGED SIDES', 3);
    };
  }

  /** Which keys the server needs to know about. Mouse look rides separately. */
  static KEYS = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ShiftLeft', 'ControlLeft', 'KeyC', 'Space'];

  /**
   * Build this frame's command from real input, predict it, and send it.
   * Replaces the local `player.update` while online.
   */
  update(dt, realInput, opts = {}) {
    const g = this.game;
    // Dead: receive only.
    //
    // The server ignores a dead player's commands and does not simulate them,
    // so predicting locally would be predicting against nothing — and the game
    // has already run the controller this frame for the death camera, so
    // stepping it again here would integrate the collapse twice.
    if (opts.passive) {
      this.client.reconcile(g.player, this.input);
      this._pingAcc += dt;
      if (this._pingAcc > 1) { this._pingAcc = 0; this.client.ping_(); }
      this.applyHealth();
      this.applyEvents();
      this.syncBodies(dt);
      this.applyRound();
      return;
    }
    const keys = [];
    const pressed = [];
    for (const k of NetSession.KEYS) {
      if (realInput.down(k)) keys.push(k);
      if (realInput.pressed(k)) pressed.push(k);
    }

    // Look is applied locally first so aiming never waits for the network, then
    // sent as an absolute angle rather than a delta — a dropped mouse delta
    // would otherwise leave the server permanently pointing somewhere else.
    const pc = g.player;
    pc.yaw -= realInput.mouse.dx * (opts.aimSensitivityScale ?? 1);
    pc.pitch -= realInput.mouse.dy * (opts.aimSensitivityScale ?? 1);
    pc.pitch = THREE.MathUtils.clamp(pc.pitch, -Math.PI / 2 + 0.02, Math.PI / 2 - 0.02);

    this.client.sendCommand(
      {
        keys, pressed, yaw: pc.yaw, pitch: pc.pitch,
        ads: !!realInput.mouse.right,
        // Plant/defuse is a HELD action, so the server needs to know the key is
        // still down every frame, not that it was pressed once.
        use: realInput.down('KeyF') || realInput.down('KeyE'),
      },
      dt, pc, this.input
    );
    this.client.reconcile(pc, this.input);

    this._pingAcc += dt;
    if (this._pingAcc > 1) { this._pingAcc = 0; this.client.ping_(); }

    this.applyHealth();
    this.applyEvents();
    this.syncBodies(dt);
    this.applyRound();
  }

  /**
   * Adopt the server's view of our own health and life.
   *
   * Online the local controller must not decide either. `damage()` is never
   * called on this client — the server resolves every shot — so health simply
   * stayed at 100 no matter how much you were hit, and the regeneration in
   * `PlayerController.update` would have papered over it anyway. Assigning it
   * from the snapshot every frame makes the server the only writer, which is
   * what it already is for everything else.
   */
  applyHealth() {
    const c = this.client;
    if (typeof c.selfHealth !== 'number') return;
    const pc = this.game.player;

    // The maximum comes from the server too — online players are tougher, and
    // a bar drawn against the offline 100 would read full at a third of it.
    if (c.maxHealth) pc.maxHealth = c.maxHealth;
    pc.health = c.selfHealth;
    // Regeneration is the server's to grant; stop the client racing it back up
    // between snapshots.
    pc.regenDelay = 1e9;

    if (c.selfAlive === false && pc.alive) {
      // Route through the same death the offline game uses so the collapse,
      // the spectator camera and the banner all behave identically. The
      // controller's own `damage()` path is bypassed, so the flags it would
      // have set are set here.
      pc.alive = false;
      pc.deathTime = 0;
      pc.deathDir = this._lastHitDir?.clone() ?? null;
      this.game._onPlayerDeath?.(pc.deathDir);
    } else if (c.selfAlive === true && !pc.alive) {
      // The SERVER decides when you are back, and it has already chosen where.
      // `respawn` would pick a local spawn point and fight the next snapshot,
      // so only the state is restored — position comes from reconciliation.
      pc.alive = true;
      pc.health = c.selfHealth;
      pc.regenDelay = 1e9;
      pc.deathTime = 0;
      pc.trauma = 0;
      this.game.spectator?.reset?.();
      this.game.weapon.ammo = this.game.weapon.spec.mag;
      this.game.postfx?.setDamage(0);
    }
  }

  /**
   * React to what the server says happened: hits landed, hits taken, kills.
   *
   * These are the feedback signals a shooter is made of, and none of them can
   * be decided locally in a server-authoritative game — this client does not
   * know whether its own shot connected.
   */
  applyEvents() {
    const g = this.game;
    const me = this.client.selfId;
    for (const ev of this.client.drainEvents()) {
      const iAmVictim = ev.v === me;
      const iAmAttacker = ev.a === me;

      // The full end-of-match roster, which only the event carries. The
      // snapshot repeats the AWARDS so a late arrival still gets a screen, but
      // repeating everyone's stats in every snapshot for twenty seconds would
      // be paying a packet for something that changes once.
      // A door or lift the server has started. Applied locally so the meshes
      // move; both sides run the same MoverSet at the same speed, so starting
      // from the same trigger keeps them together.
      if (ev.e === 'door') { g.level.trigger?.(ev.n); g.audio?.reload?.('bolt'); continue; }

      if (ev.e === 'match') { this._matchRows = ev.rows ?? []; continue; }
      if (ev.e === 'newmatch') { this._matchRows = null; continue; }

      if (ev.e === 'boom') {
        // A rocket or grenade of OUR own was drawn the instant it went off, so
        // replaying it here would double the fireball a third of a second
        // later. An airstrike is different: the server lays that run, this
        // client never simulated it, and the caller has to watch their own
        // bombs land like everyone else.
        const ours = iAmAttacker && ev.w !== 'strike';
        if (!ours && Array.isArray(ev.p)) {
          _v.set(ev.p[0], ev.p[1], ev.p[2]);
          g.effects?.explosion?.(_v,
            EXPLOSIVES[ev.w]?.radius ?? STREAK_RULES.strikeRadius);
          g.audio?.gunshot?.(_v, { close: false, gain: 1.6, voice: 'launcher' });
        }
        continue;
      }

      if (ev.e === 'uav') {
        // Your side's UAV, not everyone's. The other team learning it went up
        // is information they have not earned.
        if (ev.t === this.client.team) {
          g.streaks.uav = ev.d ?? STREAK_RULES.uavTime;
          g.hud?.showObjective?.('UAV ONLINE', 'ENEMY POSITIONS REVEALED', 2.4);
          g.audio?.uav?.();
        }
        continue;
      }

      if (ev.e === 'flash') {
        // The pop, for everyone who did not throw it — the thrower already saw
        // it go off. Whether it BLINDED you arrives separately and privately.
        if (!iAmAttacker && Array.isArray(ev.p)) {
          _v.set(ev.p[0], ev.p[1], ev.p[2]);
          g.flashbang?.(_v, false);
        }
        continue;
      }

      if (ev.e === 'heli') {
        // Everyone sees the gunship — it is a thirty-metre helicopter. Both
        // sides fly it from the server's centre and start angle, so the thing
        // shooting you is where you can see it.
        g.streaks?.removeHeli?.();
        g.streaks?._callHeli?.({ c: ev.c, ang: ev.ang, d: ev.d });
        const mine = ev.t === this.client.team;
        g.hud?.showObjective?.(mine ? 'ATTACK HELO' : 'ENEMY GUNSHIP',
          mine ? 'GUNSHIP ON STATION' : 'TAKE COVER', 2.6);
        continue;
      }

      if (ev.e === 'heliout') { g.streaks?.removeHeli?.(); continue; }

      if (ev.e === 'strike') {
        // The warning, for both sides — an airstrike you cannot hear coming is
        // just an unexplained death.
        const mine = ev.t === this.client.team;
        g.hud?.showObjective?.(mine ? 'AIRSTRIKE INBOUND' : 'INCOMING AIRSTRIKE',
          mine ? 'ON THE WAY' : 'CLEAR THE AREA', 2.6);
        g.audio?.strikeInbound?.();
        continue;
      }

      if ((ev.e === 'hit' || ev.e === 'kill') && iAmVictim) {
        // Directional damage indicator, from wherever the shooter actually is.
        const shooter = this.client.remotes.get(ev.a);
        const s = shooter && this.client.sampleRemote(shooter, _v);
        if (s) {
          _from.copy(s.pos).sub(g.player.position);
          this._lastHitDir = _from.clone().setY(0).normalize();
          g.hud?.damageFrom?.(Math.atan2(_from.x, _from.z) - g.player.yaw + Math.PI);
        } else {
          g.hud?.damageFrom?.(0);
        }
        g.player.addTrauma?.(ev.e === 'kill' ? 0.5 : 0.18);
        // Whoever shot you has given themselves away, so put them on the
        // minimap for a few seconds. Offline the bot's own AI state supplies
        // this; online it can only come from the server, because a client has
        // no way to know it is being aimed at.
        // Timestamps, not `alert = 1`: a bot decays its own alert every tick and
        // a remote rig is never ticked at all, so a flag set here would leave
        // them marked hostile for the rest of the match.
        const rig = this.bodies.get(ev.a)?.rig;
        if (rig) {
          rig._knownUntil = g.clockNow + 4;
          rig._hotUntil = g.clockNow + 4;
        }
      }

      if (iAmAttacker && (ev.e === 'hit' || ev.e === 'kill')) {
        // The kill marker outranks the headshot one, same as offline.
        g.hud?.hit?.(ev.e === 'kill' ? 'kill' : (ev.p === 'head' ? 'head' : 'body'));
        g.audio?.hitmarker?.(ev.e === 'kill');
        if (ev.e === 'kill') {
          const victim = this.client.remotes.get(ev.v);
          g.hud?.addKill?.(victim?.name ?? 'HOSTILE', g.weapon?.spec?.name ?? 'M4A1', false);
        }
      }
    }
  }

  /** Tell the server we fired, and where we were pointing. */
  fire(weaponKey) {
    const cam = this.game.engine.camera;
    cam.getWorldDirection(_v);
    try {
      this.client.ws.send(JSON.stringify({
        type: 'fire', w: weaponKey, d: [_v.x, _v.y, _v.z],
      }));
    } catch { /* closing */ }
  }

  /**
   * Report an explosion we have already simulated and drawn.
   *
   * The position is the whole message. The server knows what a rocket and a
   * grenade are worth and will not take our word for either — see
   * `Combat.explode`, which checks the point is reachable before it hurts
   * anybody.
   */
  explode(kind, pos) {
    try {
      this.client.ws.send(JSON.stringify({
        type: 'boom', w: kind,
        p: [+pos.x.toFixed(2), +pos.y.toFixed(2), +pos.z.toFixed(2)],
      }));
    } catch { /* closing */ }
  }

  /** Report a flashbang. The server works out who it blinded. */
  flash(pos) {
    try {
      this.client.ws.send(JSON.stringify({
        type: 'flash',
        p: [+pos.x.toFixed(2), +pos.y.toFixed(2), +pos.z.toFixed(2)],
      }));
    } catch { /* closing */ }
  }

  /**
   * Ask to spend a killstreak. The server holds the ledger and answers with
   * what is left, plus an event for whatever the reward actually does.
   */
  callStreak(id, at = null) {
    try {
      this.client.ws.send(JSON.stringify({
        type: 'streak', id,
        at: at ? [+at.x.toFixed(2), +at.y.toFixed(2), +at.z.toFixed(2)] : null,
      }));
    } catch { /* closing */ }
  }

  /**
   * Move every remote body to where the interpolation buffer says it is.
   *
   * Rigs are borrowed from the existing Enemy class so remote players look like
   * everything else in the game, but their AI is never ticked — a networked
   * player's decisions are already made somewhere else, and running a brain on
   * top would fight the snapshots.
   */
  syncBodies(dt = 0) {
    const g = this.game;
    for (const [id, r] of this.client.remotes) {
      let body = this.bodies.get(id);
      if (!body) {
        // Never re-body someone the server already lists as dead — that is a
        // corpse we are still playing out, or a player waiting to respawn.
        if (!r.alive) continue;
        const rig = g.spawnNetBody?.(r.team, r.op ?? 0);
        if (!rig) continue;
        // The rig arrives with a generated bot callsign. Overwrite it with the
        // name this player actually chose — it is what the spectator overlay
        // and anything else reading the rig will show.
        rig.name = r.name;
        body = { rig, pos: new THREE.Vector3(), started: false };
        this.bodies.set(id, body);
      }
      const s = this.client.sampleRemote(r, _v);
      if (!s) continue;

      // Death. The body used to simply wink out — `visible = alive` — which is
      // why a player you shot vanished mid-stride. It now plays the same
      // ragdoll every bot does, and the corpse is handed off so it can finish
      // falling while the server respawns the player behind it.
      if (!r.alive) {
        _from.copy(s.pos).sub(g.player.position).setY(0);
        if (_from.lengthSq() < 1e-6) _from.set(0, 0, 1);
        this.killBody(id, body, _from.normalize());
        continue;
      }

      // Snapshots carry the EYE. A rig stands on its feet, and the gap between
      // them is `height - capsule radius` — not the 1.62 that was hard-coded
      // here, which planted everyone 0.24 m into the ground.
      const feetY = s.pos.y - eyeOffset(s.ch);
      // What the placement actually used, for tools/net-body-probe.mjs. Read
      // across two frames these numbers are just the player walking; recorded
      // here they are one instant and mean something.
      body.offset = s.pos.y - feetY;
      const rig = body.rig;

      // Velocity for the locomotion blend. Remote bodies are never simulated,
      // so nothing else would ever set it and every player slid around in the
      // bind pose. Derived from where interpolation actually put them, which is
      // the only honest source: it already matches what you can see.
      if (body.started && dt > 0) {
        _prev.copy(body.pos);
        rig.velocity.set(
          (s.pos.x - _prev.x) / dt, 0, (s.pos.z - _prev.z) / dt,
        );
      } else {
        rig.velocity.set(0, 0, 0);
      }
      body.pos.copy(s.pos);
      body.started = true;

      rig.root.position.set(s.pos.x, feetY, s.pos.z);
      rig.root.rotation.y = s.yaw;
      rig.yaw = s.yaw;
      rig.crouched = !!s.crouch;
      rig.root.visible = true;
      // Pose only. `update()` would run the AI — decisions this player already
      // made somewhere else — so only the animation half of it is called.
      rig._animate?.(dt);
    }

    // Corpses keep solving after their player has gone.
    if (this._corpses?.length) {
      for (const c of this._corpses) c._updateDeath?.(dt);
      this._corpses = this._corpses.filter((c) => {
        if (!c._removed) return true;
        g.despawnNetBody?.(c);
        return false;
      });
    }

    // Anyone the server stopped reporting has left.
    for (const [id, body] of this.bodies) {
      if (!this.client.remotes.has(id)) {
        g.despawnNetBody?.(body.rig);
        this.bodies.delete(id);
      }
    }
  }

  /**
   * Turn a live body into a ragdoll and forget about it.
   *
   * The rig leaves `bodies` immediately, so the next snapshot that shows this
   * player alive again builds them a fresh one — the corpse is not a thing that
   * can stand back up, and reusing it would mean un-ragdolling a skeleton.
   */
  killBody(id, body, dir) {
    this.bodies.delete(id);
    const rig = body.rig;
    rig.kill?.(dir, 'body', null);
    (this._corpses ??= []).push(rig);
  }

  /**
   * Push the server's round state into the HUD.
   *
   * The client keeps its own mode object for the offline game, but online it is
   * never started — everything shown here comes from the server, so two players
   * cannot disagree about whose round it is.
   */
  applyRound() {
    const r = this.client.round;
    if (!r) return;
    const g = this.game;
    const hud = g.hud;
    if (!hud) return;

    hud.netRound = r;
    if (r.phase !== this._lastPhase) {
      this._lastPhase = r.phase;
      if (r.phase === 'freeze') hud.showObjective?.(`ROUND ${r.round}`, 'GET READY', 2.5);
      else if (r.phase === 'live') hud.showObjective?.('GO', '', 1.2);
      // Nothing for 'over' — `_drawRoundResult` owns the end of a round, and a
      // banner underneath it just says the same thing twice in two sizes.
    }
    if (r.bomb?.s === 'planted' && !this._announcedPlant) {
      this._announcedPlant = true;
      hud.showObjective?.('BOMB PLANTED', `SITE ${r.bomb.site ?? ''}`, 3);
    }
    if (r.bomb?.s !== 'planted') this._announcedPlant = false;
  }

  /**
   * Everything the round HUD draws, taken from the server.
   *
   * The offline modes supply this through `mode.decorateHud`, but online the
   * local mode object is never started or ticked — so it kept reporting the
   * state it was constructed with, and the round clock sat frozen at 0:04 (its
   * initial freeze time) for an entire match. Nothing here is decided locally;
   * every value is whatever the last snapshot said.
   */
  decorateHud(s) {
    const r = this.client.round;
    if (!r) return;
    const bomb = r.bomb;
    const planted = bomb?.s === 'planted';

    // The HUD treats 'planted' as a phase of its own — that is how it knows to
    // show the fuse instead of the round clock.
    s.roundPhase = planted ? 'planted' : r.phase;
    s.roundClock = Math.max(0, r.clock ?? 0);
    s.round = r.round ?? 1;
    s.roundWinner = r.winner ?? null;
    s.roundReason = r.reason ?? null;
    s.matchWinner = r.matchWinner ?? null;

    /*
     * The award screens, raised and lowered off the server's own state.
     *
     * Driven from the snapshot rather than from the one-shot `match` event: the
     * event is delivered at most once, and anyone who joined, lagged or
     * reconnected during the twenty-second intermission would sit on a blank
     * screen with no way to ask for it again. `show` ignores a repeat of the
     * same match, so re-offering it every snapshot costs nothing.
     */
    if (r.matchWinner && r.awards) {
      this.game.matchAwards.show({
        winner: r.matchWinner,
        red: this.client.score?.red ?? 0,
        blue: this.client.score?.blue ?? 0,
        mine: this.client.team ?? null,
        ffa: false,
        awards: r.awards,
        rows: this._awardRows(r.awards),
      });
    } else if (this.game.matchAwards.active) {
      this.game.matchAwards.hide();
    }
    s.roundsToWin = r.toWin ?? null;
    s.roundsPerMatch = r.ofRounds ?? null;
    s.teamScore = {
      red: this.client.score?.red ?? 0,
      blue: this.client.score?.blue ?? 0,
      mine: this.client.team ?? null,
    };
    s.playerTeam = this.client.team ?? null;
    s.bombFuse = planted ? bomb.f : null;
    s.bombState = bomb?.s ?? null;
    s.bombSiteLive = planted ? bomb.site : null;
    s.carryingBomb = !!bomb && bomb.c === this.client.selfId;

    // The plant/defuse bar. The server sends whoever is furthest along, so a
    // defender sees the plant they are running at fill up — which is the point.
    s.progress = r.progress
      ? { t: r.progress.f, kind: r.progress.kind === 'plant' ? 'PLANT' : 'DEFUSE' }
      : null;

    // Where a planted bomb is, for the world marker.
    if (planted && bomb.p) {
      s.bombWorldPos = (this._bombPos ??= new THREE.Vector3())
        .set(bomb.p[0], bomb.p[1], bomb.p[2]);
      s.bombMarker = `BOMB ${bomb.site ?? ''}`.trim();
    } else {
      s.bombWorldPos = null;
      s.bombMarker = null;
    }
  }

  /**
   * Final-scoreboard rows for the award screen.
   *
   * Built from the award table's own rows rather than from `client.remotes`,
   * because remotes only contains players with a live body — anyone dead at the
   * final whistle, which in bomb defusal is most of a side, is simply not in
   * there. The server already sent everybody; use that.
   */
  _awardRows(awards) {
    const seen = new Map();
    for (const a of awards) {
      const r = a.winner;
      if (r && !seen.has(r.id)) seen.set(r.id, r);
    }
    // The event carries the full roster too; prefer it when it arrived.
    for (const r of this._matchRows ?? []) seen.set(r.id, r);
    const selfId = String(this.client.selfId);
    return [...seen.values()].map((r) => ({ ...r, self: r.id === selfId }));
  }

  /** Ask the server to press a button. See Game.update's use verb. */
  pressButton() { this.client.pressButton(); }

  /** Scoreboard rows for the HUD. */
  scoreboard() {
    const rows = [{
      name: 'YOU', team: this.client.team, kills: 0, deaths: 0, ping: this.client.ping, self: true,
    }];
    for (const r of this.client.remotes.values()) {
      rows.push({ name: r.name, team: r.team, kills: r.kills, deaths: r.deaths, ping: 0 });
    }
    return rows;
  }
}
